<template>
  <div class="workflow-page">
    <CommonOverlay :show="showOverlay" title="Processing Workflow" message="Please wait while we load your workflows..." style="position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; height: 100% !important; background-color: rgba(0, 0, 0, 0.7) !important; display: flex !important; justify-content: center !important; align-items: center !important; z-index: 9999999 !important;" />

    <!-- Modern Header matching Forms/Wiki style -->
    <div class="workflow-header">
      <div class="header-left">
        <h1 class="page-title">Workflows</h1>
      </div>
      <div class="header-actions">
        <b-button v-if="$can('update', 'workflow')" variant="success" class="create-btn" @click="gotoCreatePage()">
          <i class="fa fa-plus"></i> New Workflow
        </b-button>
      </div>
    </div>

    <ErrorDisplay/>

    <!-- Workflow Panel -->
    <div class="workflow-panel">
      <div class="panel-card">
        <div class="panel-header">
          <h5 class="section-title">
            <i class="fa fa-sitemap section-icon"></i>
            Your Workflows
          </h5>
        </div>

        <!-- Empty State -->
        <div class="empty-state" v-if="workflows.length === 0 && !showOverlay">
          <div class="empty-icon">
            <i class="fa fa-sitemap"></i>
          </div>
          <h4>No Workflows Found</h4>
          <p>Get started by creating your first workflow</p>
          <button v-if="$can('update', 'workflow')" type="button" class="btn btn-primary create-first-btn" @click="gotoCreatePage()">
            <i class="fa fa-plus"></i> Create Workflow
          </button>
        </div>

        <!-- Workflow Table -->
        <div v-if="workflows.length > 0" class="workflow-table-container">
          <!-- Table Header -->
          <div class="workflow-table-header">
            <div>Trigger</div>
            <div>Name</div>
            <div class="header-created-by">Created By</div>
            <div class="header-created">Created On</div>
            <div>Last Updated</div>
            <div class="header-output">Output</div>
            <div>Active</div>
            <div class="text-right">Actions</div>
          </div>

          <!-- Workflow Items -->
          <div
            class="workflow-item"
            v-for="(workflow, index) in workflows"
            :key="index"
            @click="gotoViewPage(workflow.WorkflowID)"
          >
            <!-- Trigger Column -->
            <div class="trigger-column">
              <i class="fa fa-bolt trigger-icon" aria-hidden="true"></i>
              <span class="trigger-badge">{{ workflow.TriggerOn }}</span>
            </div>

            <!-- Name Column -->
            <div class="name-column">
              <div v-html="workflow.Name"></div>
            </div>

            <!-- Created By Column -->
            <div class="created-by-column">
              <span>{{ getEmployeeName(workflow.CreatedByEmpID) }}</span>
            </div>

            <!-- Created On Column -->
            <div class="date-column created-on-column">
              {{ workflow.CreatedOn | dateformat(dateFormatStr) }}
            </div>

            <!-- Last Updated Column -->
            <div class="date-column">
              {{ workflow.LastUpdated | dateformat(dateFormatStr) }}
            </div>

            <!-- Output Column -->
            <div class="output-column" @click.stop>
              <a href="#" class="output-link" @click="gotoWorkflowOutputPage(workflow.WorkflowID)">
                <i class="fa fa-external-link"></i> View
              </a>
            </div>

            <!-- Active Toggle Column -->
            <div class="active-column" @click.stop>
              <b-form-checkbox
                :id="`toggle-workflow-${workflow.WorkflowID}`"
                v-model="workflow.Active"
                switch
                size="lg"
                :disabled="processingWorkflowId === workflow.WorkflowID"
              ></b-form-checkbox>
              <b-tooltip v-if="elementExists(`toggle-workflow-${workflow.WorkflowID}`)" class="tool_tip" :target="`toggle-workflow-${workflow.WorkflowID}`" triggers="hover">
                {{ processingWorkflowId === workflow.WorkflowID ? 'Processing...' : 'Toggle to Enable or Disable workflow' }}
              </b-tooltip>
            </div>

            <!-- Actions Column -->
            <div class="actions-container" @click.stop>
              <i
                v-if="$can('update', 'workflow')"
                :id="`modify-workflow-${workflow.WorkflowID}`"
                class="fa fa-pencil action-icon"
                @click="gotoViewPage(workflow.WorkflowID)"
              ></i>
              <b-tooltip v-if="$can('update', 'workflow') && elementExists(`modify-workflow-${workflow.WorkflowID}`)" class="tool_tip" :target="`modify-workflow-${workflow.WorkflowID}`" placement="top" triggers="hover">
                Modify Workflow
              </b-tooltip>

              <i
                v-if="$can('delete', 'workflow')"
                :id="`delete-workflow-${workflow.WorkflowID}`"
                class="fa fa-trash action-icon delete-icon"
                @click="showModal('delete', workflow.WorkflowID)"
              ></i>
              <b-tooltip v-if="elementExists(`delete-workflow-${workflow.WorkflowID}`)" class="tool_tip" :target="`delete-workflow-${workflow.WorkflowID}`" placement="top" triggers="hover">
                Delete Workflow
              </b-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Modal -->
    <b-modal ref="delete" id="delete" title="Delete Workflow" body-class="bg-white" centered>
      <div class="delete-modal-content">
        <i class="fa fa-exclamation-triangle warning-icon"></i>
        <p>Are you sure you want to delete this workflow?</p>
        <p class="warning-text">This action cannot be undone.</p>
      </div>
      <div slot="modal-footer" class="modal-footer-btns">
        <b-button variant="outline-secondary" @click="hideModal('delete')">Cancel</b-button>
        <b-button variant="danger" @click="del">Delete</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import Multiselect from "vue-multiselect";
import { mapState, mapActions, mapMutations } from 'vuex';
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import CommonOverlay from "@/components/CommonOverlay";
import mixin from '../../mixin.js';

export default {
  name: "Workflow",
  components: { Multiselect, ErrorDisplay, CommonOverlay },
  props: ['searchTerm'],
  mixins: [mixin],

  created() {
    if(this.allSettings)
        this.init();
  },

  data() {
    return {
      showOverlay: false,
      workflows: [],
      selectedWorkflowID: 0,
      simpleDropdownVariant: "Select",
      isPickerActive: false,
      lastWorkflowActionErrored: false,
      processingWorkflowId: null  // Track which workflow is currently being enabled/disabled
    };
  },
  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('workflow', ['getAllWorkflows', 'deleteWorkflow', 'enableWorkflow', 'disableWorkflow']),

    async init() {
      this.showOverlay = true;
      if(this.workflows.length === 0) {
        this.workflows = await this.getAllWorkflows({siteID: this.siteID});
        if(this.workflows && Array.isArray(this.workflows)) {
          this.workflows.map(w => w.Active = Boolean(w.Active));
        }
      }
      this.showOverlay = false;
    },

    showModal(name, workflowID) {
      if (name === 'delete' && !this.assertCan('delete', 'workflow')) return;
      this.selectedWorkflowID = workflowID;
      this.$refs[name].show();
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    gotoCreatePage() {
      if (!this.assertCan('update', 'workflow')) return;
      let routeData = this.$router.resolve({name: 'CreateWorkflow'});
      window.open(routeData.href, '_blank');
    },

    gotoWorkflowOutputPage(workflowID) {
      let routeData = this.$router.resolve({name: 'WorkflowOutput', params: {workflowID:  `${workflowID}`}});
      window.open(routeData.href, '_blank');
    },

    gotoViewPage(workflowID) {
      if (!this.assertCan('update', 'workflow')) return;
      let routeData = this.$router.resolve(
        {
          path: `workflow/${workflowID}`
        }
      );
      window.open(routeData.href, '_blank');
    },

    async del() {
      if (!this.assertCan('delete', 'workflow')) return;
      try {
        this.hideModal('delete');
        this.showOverlay = !this.showOverlay;
        await this.deleteWorkflow({WorkflowID: this.selectedWorkflowID});
        this.showOverlay = !this.showOverlay;
        this.$router.go(0);
      } catch(e) {
        this.showOverlay = !this.showOverlay;
        console.log('Error' + e);
      }
    },

    elementExists(id) {
      return !!document.getElementById(id);
    }
  },

  computed: {
    enabled() {
      return this.workflows.map(w => w.Active);
    },
  },

  watch: {
    allSettings(newValue, oldValue) {
      if(!oldValue) {
        this.init();
      }
    },

    enabled: {
     async handler(newValue, oldValue) {
       if(oldValue.length) {
        const indexOfChangedWorkflow = newValue.findIndex(function (v, i) { return v !== oldValue[i] });
        let workflowChanged = this.workflows[indexOfChangedWorkflow];

        // Prevent rapid toggling - if already processing this workflow, ignore
        if(this.processingWorkflowId === workflowChanged.WorkflowID) {
          console.log(`Workflow ${workflowChanged.WorkflowID} is already being processed, ignoring toggle`);
          return;
        }

        console.dir(workflowChanged);

        // Mark this workflow as being processed
        this.processingWorkflowId = workflowChanged.WorkflowID;

        if(workflowChanged.Active) {
          try {
            this.showOverlay = !this.showOverlay;
            await this.enableWorkflow({WorkflowID: workflowChanged.WorkflowID});
            this.showOverlay = !this.showOverlay;
          } catch(err) {
            this.showOverlay = !this.showOverlay;
            console.log(`Caught error while trying to enable workflow`);
            this.lastWorkflowActionErrored = true;
          } finally {
            this.processingWorkflowId = null;
          }
        } else {
          try {
            this.showOverlay = !this.showOverlay;
            await this.disableWorkflow({WorkflowID: workflowChanged.WorkflowID});
            this.showOverlay = !this.showOverlay;
          } catch(err) {
            this.showOverlay = !this.showOverlay;
            console.log(`Caught error while trying to disable workflow`);
          } finally {
            this.processingWorkflowId = null;
          }
        }
       }
     },
    }
  }

};
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style src="./Workflow.scss" lang="scss" scoped></style>
